import type { FileProps } from "@shared-kernel/domain/types/file-validation";
import type { RequestState } from "src/redux/state/shared-types";
import { v4 as uuidv4 } from "uuid";

export const getNormalizedString = (s: string) => {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "'");
};

const generateRandomLetter = (): string => String.fromCharCode(97 + Math.floor(Math.random() * 26));
export const generateRandomString = (length: number): string => {
  let res = "";
  for (let index = 0; index < length; index++) {
    res += generateRandomLetter();
  }
  return res;
};

export const urlToBloB = (url: string) => fetch(url).then(response => response.blob());

export function assertArrLength<T>(array: T[], expectedLength: number): asserts array is [T, T, T, T, T, T, T, T, T, T] {
  if (array.length !== expectedLength) {
    throw new Error("Array must have exactly 10 items");
  }
}

export function isLoadingState(state: RequestState) {
  return ["pending"].includes(state);
}

export function isReadyState(state: RequestState) {
  return ["success"].includes(state);
}

/**
 * Checks if a request state indicates loading for list/array data.
 * For lists, we want to show loading during both "pending" and "idle" states
 * to prevent showing empty tables while data is being fetched.
 *
 * @param state - The request state from Redux
 * @returns true if the state indicates loading (pending or idle)
 *
 * @example
 * const { data, fetching } = useAppSelector(selectListData);
 * <DataTable data={data} isLoading={isLoadingListState(fetching)} />
 */
export function isLoadingListState(state: RequestState): boolean {
  return state === "pending" || state === "idle";
}

/**
 * Combines multiple RequestState values into a single state.
 * Priority: failed > pending > idle > success
 */
export function combineFetchingStates(...states: RequestState[]): RequestState {
  if (states.some(s => s === "failed")) return "failed";
  if (states.some(s => s === "pending")) return "pending";
  if (states.some(s => s === "idle")) return "idle";
  return "success";
}

export const generateNewId = () => uuidv4();

export const sortAlphabetically = (a: string, b: string) => {
  return getNormalizedString(a).localeCompare(getNormalizedString(b));
};

export const getFileProps = async (file: File): Promise<FileProps> => {
  return {
    fileSize: file.size,
    type: file.type,
  };
};

/**
 * Recursively counts the number of form validation errors in a form error object
 * @param errors - The form error object from react-hook-form
 * @returns The total number of validation errors
 */
export const getErrorCount = (errors: Record<string, unknown>): number => {
  let count = 0;
  const countErrors = (obj: Record<string, unknown>) => {
    for (const key in obj) {
      if (obj[key]) {
        const value = obj[key];
        if (value && typeof value === "object") {
          if ("message" in value) count++;
          else countErrors(value as Record<string, unknown>);
        }
      }
    }
  };
  countErrors(errors);
  return count;
};
